import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FlaskConical, Sparkles } from "lucide-react";
import api from "../api";
import ProductCard from "../components/ProductCard";
import Testimonials from "../components/Testimonials";
import TrustSection from "../components/TrustSection";
import BestSellersStrip from "../components/BestSellersStrip";
import ResearchStacks from "../components/ResearchStacks";
import SEO, { buildOrganizationJsonLd, buildWebSiteJsonLd, buildLocalBusinessJsonLd } from "../components/SEO";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products").then(({ data }) => setFeatured(data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div>
      <SEO
        title="Research-Grade Peptides · Lab-Verified · US Domestic Shipping"
        description="Premium research-grade peptides for licensed researchers. BPC-157, Tirzepatide, Retatrutide, NAD+, GLP-1 peptides and more. 99%+ HPLC verified purity, tiered volume pricing, fast US domestic shipping. For research purposes only."
        path="/"
        keywords="research peptides, buy peptides USA, BPC-157, Tirzepatide, Retatrutide, NAD+, peptide supplier, lab verified peptides, research grade peptides, peptide nasal spray, Selank nasal spray, Semax nasal spray"
        jsonLd={[buildOrganizationJsonLd(), buildWebSiteJsonLd(), buildLocalBusinessJsonLd()]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="grid-lines absolute inset-0 opacity-30 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 pb-32 relative pt-14 sm:pt-20">
          <div data-testid="hero-motto" className="mb-10 reveal leading-[0.95]" style={{ fontFamily: "Michroma, sans-serif" }}>
            {["Precision.", "Purity.", "Performance."].map((w) => (
              <span key={w} className="block sm:inline sm:mr-5 text-[30px] sm:text-4xl lg:text-5xl uppercase bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(180deg,#FFFFFF 0%,#BFE9FF 45%,#00C3FF 100%)" }}>
                {w}
              </span>
            ))}
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light leading-[1.05] tracking-tight max-w-4xl reveal reveal-delay-1">
            Research compounds<br />
            engineered for <span className="text-[#00C3FF]">results.</span>
          </h1>
          <p className="text-base sm:text-lg text-[#8A94A6] max-w-xl mt-8 leading-relaxed reveal reveal-delay-2">
            ProSource Labs is a direct-to-researcher retail store — buy a single vial, no minimum
            order. Third-party lab tested, shipped from Southern California.
          </p>
          <div className="flex flex-wrap gap-4 mt-10 reveal reveal-delay-3">
            <Link to="/shop" data-testid="hero-shop-cta" className="btn-primary">
              Browse Catalog <ArrowRight size={14} />
            </Link>
            <Link to="/about" className="btn-ghost">Our Standards</Link>
          </div>

          <div className="mt-24 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {[
              { icon: ShieldCheck, label: "Third-Party Tested", value: "≥99%" },
              { icon: FlaskConical, label: "Lyophilized Vials", value: "Research-Grade" },
              { icon: Sparkles, label: "Cold-Chain Shipped", value: "24h Dispatch" },
            ].map((s) => (
              <div key={s.label} className="bg-[#0C0C12] p-8 flex flex-col gap-4">
                <s.icon className="text-[#00C3FF]" size={20} />
                <div>
                  <div className="overline" style={{ fontSize: 10 }}>{s.label}</div>
                  <div className="font-mono-tech text-2xl mt-2">{s.value}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">FEATURED COMPOUNDS</div>
            <h2 className="font-heading text-3xl sm:text-4xl mt-3">Top of the catalog.</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-[0.2em] text-[#00C3FF] hover:text-white">
            All Products <ArrowRight size={12} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          {featured.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      </section>

      <BestSellersStrip />

      <ResearchStacks />

      <Testimonials />

      <TrustSection />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="surface p-12 sm:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 relative overflow-hidden">
          <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full opacity-20" style={{ background: "radial-gradient(circle, #00C3FF, transparent 70%)" }} />
          <div className="relative">
            <div className="overline">RESEARCH ACCESS</div>
            <h3 className="font-heading text-3xl sm:text-4xl mt-3 max-w-xl">
              Pharmaceutical-grade, lab-verified, ready to ship.
            </h3>
          </div>
          <Link to="/shop" className="btn-primary relative">Start Browsing <ArrowRight size={14} /></Link>
        </div>
      </section>
    </div>
  );
}
